import {
  Box,
  Breadcrumbs,
  CardMedia,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  Link,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DateRangePickerDay, { DateRangePickerDayProps } from '@mui/lab/DateRangePickerDay';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import StaticDateRangePicker from '@mui/lab/StaticDateRangePicker';
import { CommonLayout } from '../commonComponents';
import { DataTable, ColumnType } from '../DataTable';
import { getApplicationSummary } from '../../redux-store/actions/application';
import { getFundsList } from '../../redux-store/actions/funds';
import {
  Distributor,
  getDistributorsListForFilters,
  SubDistributor,
} from '../../redux-store/actions';
import {
  ApplicationSummaryItem,
  ApplicationSummaryResponse,
  ApplicationSummaryViewAs,
} from '../../redux-store/types/application';
import { FundProps, GetFundsListResponseBody } from '../../redux-store/types/funds';
import { formatCurrency } from '../../utils/utilityFunctions';
import { useDebounce } from '../../utils/useDebounce';
import { formatShortDate } from '../../utils/date';
import { EntityDetailsLocationState } from './EntityDetails';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DistributorTypesForUserManageMent, subdistributorTypes } from '../../utils/constant';
import BusinessIcon from '@mui/icons-material/Business';

const PAGE_LIMIT = 10;

type TabKey = 'individual' | 'corporate' | 'rm';

const TABS: { key: TabKey; label: string; viewAs: ApplicationSummaryViewAs; type?: string }[] = [
  { key: 'individual', label: 'Individual Distributor', viewAs: 'distributor', type: 'individual' },
  { key: 'corporate', label: 'Corporate Distributor', viewAs: 'distributor', type: 'company' },
  { key: 'rm', label: 'AMC RM', viewAs: 'rm' },
];

const RangeDay = (props: DateRangePickerDayProps<Date>) => (
  <DateRangePickerDay
    {...props}
    sx={{
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
        backgroundColor: '#E8ECF0',
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart, &.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd':
        {
          backgroundColor: 'transparent',
        },
      '& .MuiDateRangePickerDay-day': {
        fontSize: 13,
        fontWeight: 500,
        color: '#5F6B7A',
        borderRadius: '4px',
        '&.Mui-selected': {
          backgroundColor: '#2057A6',
          color: '#FFFFFF',
          fontWeight: 600,
          '&:hover, &:focus': {
            backgroundColor: '#2057A6',
          },
        },
        '&.MuiDateRangePickerDay-dayInsideRangeInterval': {
          color: '#293139',
          fontWeight: 600,
        },
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart .MuiDateRangePickerDay-day': {
        borderTopLeftRadius: '4px',
        borderBottomLeftRadius: '4px',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day': {
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day':
        {
          borderRadius: '4px',
        },
    }}
  />
);

const formatAmount = (amount?: number | null): string => {
  return `₹ ${formatCurrency(amount || 0, 'INR')}`;
};

export default function DistributorUserData(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<TabKey>(TABS[0].key);
  const [search, setSearch] = useState('');
  const [schemeId, setSchemeId] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>('');
  const [funds, setFunds] = useState<FundProps[]>([]);
  const [companies, setCompanies] = useState<Distributor[]>([]);
  const [rows, setRows] = useState<ApplicationSummaryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [noOfPages, setNoOfPages] = useState(1);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);
  const debounceSearch = useDebounce(search, 500);

  const tabConfig = TABS.find((tab) => tab.key === activeTab) || TABS[0];
  const isRmTab = activeTab === 'rm';
  // const [filters, setFilters] = useState({
  //   schemeId: '',
  //   companyType: DistributorTypesForUserManageMent.Company,
  // });
  const [distributorType, setDistributerType] = useState(
    // location.state?.distributorType ? location.state.distributorType :
    'company'
  );
  const isCorporateTab = activeTab === 'corporate';
  // const [subDistributorId, setSubDistributorId] = useState<string>('');
  // const [subDistributors, setSubDistributors] = useState<SubDistributor[]>([]);
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<ApplicationSummaryItem | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let isActive = true;
    (async function () {
      try {
        const { funds: fundsList } = (await dispatch(
          getFundsList()
        )) as unknown as GetFundsListResponseBody;
        if (!isActive) return;
        setFunds(fundsList || []);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!isCorporateTab) return;

    let isActive = true;

    (async () => {
      try {
        const response = await dispatch(
          getDistributorsListForFilters({
            type: 'company',
          })
        );

        if (!isActive) return;

        setCompanies((response as unknown as Distributor[]) || []);
      } catch (error) {
        console.error(error);

        if (isActive) {
          setCompanies([]);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [isCorporateTab, dispatch]);

  // useEffect(() => {
  //   if (isRmTab || filters.companyType !== DistributorTypesForUserManageMent.Company) {
  //     setSubDistributors([]);
  //     setSubDistributorId('');
  //     return;
  //   }

  //   let isActive = true;

  //   const fetchSubDistributors = async () => {
  //     try {
  //       const response = await dispatch(
  //         getDistributorsListForFilters({
  //           type: 'subdistributor',
  //         })
  //       );
  //       if (!isActive) return;
  //       const subDistributorList = (response as unknown as SubDistributor[]) || [];
  //       setSubDistributors(subDistributorList);
  //     } catch (error) {
  //       if (isActive) {
  //         setSubDistributors([]);
  //       }
  //     }
  //   };
  //   fetchSubDistributors();
  //   return () => {
  //     isActive = false;
  //   };
  // }, [filters.companyType, isRmTab, dispatch]);

  useEffect(() => {
    let isActive = true;
    (async function () {
      try {
        setIsLoading(true);
        const params: {
          viewAs: ApplicationSummaryViewAs;
          type?: string;
          search?: string;
          page: number;
          limit: number;
          schemeId?: string;
          distributorId?: string;
          subDistributorId?: string;
          from?: string;
          to?: string;
        } = {
          viewAs: tabConfig.viewAs,
          page: currentPage,
          limit: PAGE_LIMIT,
        };
        if (!isRmTab) {
          params.type = isCorporateTab ? 'company' : tabConfig.type;
        }
        if (debounceSearch.trim()) {
          params.search = debounceSearch.trim();
        }
        if (schemeId) {
          params.schemeId = schemeId;
        }
        if (isCorporateTab && companyId) {
          params.distributorId = companyId;
        }
        if (fromDate) {
          const startOfDay = new Date(fromDate);
          startOfDay.setHours(0, 0, 0, 0);
          params.from = startOfDay.toISOString();
        }
        if (toDate) {
          const endOfDay = new Date(toDate);
          endOfDay.setHours(23, 59, 59, 999);
          params.to = endOfDay.toISOString();
        }
        // if (isCorporateTab && subDistributorId) {
        //   params.subDistributorId = subDistributorId;
        // }
        const response = (await dispatch(
          getApplicationSummary(params)
        )) as unknown as ApplicationSummaryResponse;
        if (!isActive) return;
        setRows(response?.data || []);
        setNoOfPages(response?.pages || 1);
      } catch (e) {
        console.error((e as Error).message);
        if (isActive) {
          setRows([]);
          setNoOfPages(1);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      isActive = false;
    };
  }, [
    activeTab,
    debounceSearch,
    currentPage,
    schemeId,
    companyId,
    fromDate,
    toDate,
    tabConfig.viewAs,
    tabConfig.type,
    isRmTab,
    isCorporateTab,
    // subDistributorId,
    // filters.companyType,
  ]);

  const handleTabChange = (key: TabKey) => {
    setActiveTab(key);

    history.replace({
      pathname: history.location.pathname,
      search: `?tab=${key}`,
    });
    setCurrentPage(1);
    setSearch('');
    setSchemeId('');
    setCompanyId('');
    setFromDate(null);
    setToDate(null);
    // setSubDistributorId('');
    // setSubDistributors([]);
  };

  const handleSchemeChange = (event: SelectChangeEvent) => {
    setSchemeId(event.target.value);
    setCurrentPage(1);
  };

  const handleCompanyChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setCompanyId(value);
    // setSubDistributorId('');
    setCurrentPage(1);

    const selectedCompany = companies.find((company) => String(company.id) === String(value));

    const directSubDistributors =
      selectedCompany?.subdistributors?.filter(
        (subDistributor) =>
          subdistributorTypes[subDistributor.subdistributorType || ''] ===
          subdistributorTypes.direct
      ) || [];

    // setSubDistributors(directSubDistributors as unknown as SubDistributor[]);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };
  const handleActionClick = (event: React.MouseEvent<HTMLElement>, row: ApplicationSummaryItem) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedRow(null);
  };

  const handleOpenDetails = (row: ApplicationSummaryItem) => {
    const entityId = isRmTab ? row.rmId : row.distributorId;

    if (!entityId) return;

    const state: EntityDetailsLocationState = {
      entityId,
      viewAs: tabConfig.viewAs,
      entityName: (isRmTab ? row.rmName : row.distributorName) || 'Details',
      isCorporate: isCorporateTab,
    };
    history.push('/user-statistics/details', state);
  };

  const tableHeader: ColumnType[] = useMemo(() => {
    const nameHeader: ColumnType = isRmTab
      ? {
          header: 'AMC RM Name',
          valueGetter: (row: ApplicationSummaryItem) => row.rmName || 'N/A',
          align: 'left',
        }
      : isCorporateTab
      ? {
          header: 'Corporate Distributor Name',
          valueGetter: (row: ApplicationSummaryItem) => row.distributorName || 'N/A',
          align: 'left',
        }
      : {
          header: 'Individual Distributor Name',
          valueGetter: (row: ApplicationSummaryItem) => row.distributorName || 'N/A',
          align: 'left',
        };

    const columns: ColumnType[] = [nameHeader];

    if (!isRmTab) {
      columns.push(
        {
          header: 'PAN',
          valueGetter: (row: ApplicationSummaryItem) => row.pan || 'N/A',
        },
        {
          header: 'ARN Number',
          valueGetter: (row: ApplicationSummaryItem) => row.arnNumber || 'N/A',
        }
      );
    }

    columns.push(
      {
        header: 'Total Applications',
        valueGetter: (row: ApplicationSummaryItem) => row.totalApplications ?? 0,
      },
      {
        header: () => (
          <>
            Total Value
            <br />
            (in INR)
          </>
        ),
        mobileHeader: 'Total Value (INR)',
        valueGetter: (row: ApplicationSummaryItem) => formatAmount(row.totalValue),
      },
      {
        header: () => (
          <>
            Processed
            <br />
            (in INR)
          </>
        ),
        mobileHeader: 'Processed (INR)',
        valueGetter: (row: ApplicationSummaryItem) => formatAmount(row.processed),
      },
      {
        header: () => (
          <>
            In Pipeline
            <br />
            (in INR)
          </>
        ),
        mobileHeader: 'In Pipeline (INR)',
        valueGetter: (row: ApplicationSummaryItem) => formatAmount(row.pipeline),
      },
      {
        header: 'Actions',
        renderCell: (row: ApplicationSummaryItem) => (
          <IconButton size="small" aria-label="View details" onClick={() => handleOpenDetails(row)}>
            <CardMedia
              component="img"
              src="/images/action-arrow.svg"
              alt=""
              sx={{ width: 20, height: 20 }}
            />
          </IconButton>
        ),
      }
    );

    return columns;
  }, [isRmTab, isCorporateTab, tabConfig.viewAs]);

  const searchPlaceholder = isRmTab
    ? 'Search by RM Name'
    : 'Search by Distributor Name/PAN/ARN Number';

  const dateLabel =
    fromDate || toDate
      ? `${fromDate ? formatShortDate(fromDate) : 'From'} - ${
          toDate ? formatShortDate(toDate) : 'To'
        }`
      : 'From - To Date';

  return (
    <CommonLayout contentBgcolor="#FFFFFF">
      <Box
        sx={{
          fontFamily: 'Roboto, sans-serif',
          '& .MuiTypography-root, & .MuiTypography-h6, & .MuiTypography-body1, & .MuiButton-root, & .MuiInputBase-root, & .MuiInputBase-input, & .MuiTableCell-root, & .MuiChip-root, & .MuiChip-label, & .MuiLink-root, & .MuiTab-root, & .MuiMenuItem-root, & .MuiFormLabel-root, & .MuiSelect-select, & .MuiPaginationItem-root, & .MuiFormControlLabel-label, & .MuiListItemText-primary, & .MuiBreadcrumbs-root, & .MuiGrid-root':
            {
              fontFamily: 'Roboto, sans-serif !important',
            },
        }}>
        <Breadcrumbs
          separator=">"
          aria-label="breadcrumb"
          sx={{
            mb: 1.5,
            '& .MuiBreadcrumbs-separator': { mx: 0.75, color: '#6C737F' },
            '.MuiTypography-root, .MuiLink-root': {
              fontSize: 14,
              color: '#111927',
              fontFamily: 'Roboto, sans-serif',
            },
          }}>
          <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
            Home
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 500, color: '#6C737F !important' }}>
            User Statistics
          </Typography>
        </Breadcrumbs>

        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: { xs: 18, sm: 24 },
            fontWeight: 600,
            color: '#1A2B3C',
            mb: 2.5,
          }}>
          User Statistics
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: '4px',
            mb: 3.5,
            borderBottom: '1px solid #E5E9EF',
            width: '100%',
            overflowX: 'auto',
          }}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <Box
                key={tab.key}
                component="button"
                type="button"
                onClick={() => handleTabChange(tab.key)}
                aria-pressed={isActive}
                sx={{
                  position: 'relative',
                  minWidth: {
                    xs: 100,
                    sm: 120,
                  },
                  height: 46,
                  px: {
                    xs: 2,
                    sm: 3,
                  },
                  border: 'none',
                  outline: 'none',
                  cursor: 'pointer',
                  borderRadius: '7px 7px 0 0',
                  backgroundColor: isActive ? '#2057A6' : '#F6F6F6',
                  color: isActive ? '#FFFFFF' : '#111927',
                  fontFamily: 'Roboto, sans-serif',
                  fontStyle: 'normal',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: 1,
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isActive ? '#2057A6' : '#7777771c',
                  },
                }}>
                {tab.label}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            bgcolor: 'common.white',
            borderRadius: '12px',
            border: '1px solid #E8ECF0',
            boxShadow: '0px 4px 16px rgba(26, 43, 60, 0.04)',
            p: { xs: 2, sm: 3 },
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mb: 3.5,
              width: '100%',
              minWidth: 0,
              py: 1,
              px: 0.75,
              overflow: 'visible',
            }}>
            <FormControl
              size="small"
              sx={{
                flex: '1 1 160px',
                minWidth: 0,
                maxWidth: { xs: '100%', sm: 220 },
                overflow: 'visible',
              }}>
              <Select
                displayEmpty
                value={schemeId}
                onChange={handleSchemeChange}
                IconComponent={KeyboardArrowDownIcon}
                inputProps={{ 'aria-label': 'Filter by scheme' }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}>
                        <CardMedia
                          component="img"
                          src="/images/rupee.svg"
                          alt=""
                          sx={{
                            width: 18,
                            height: 18,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: '#293139',
                          }}>
                          Scheme
                        </Typography>
                      </Box>
                    );
                  }
                  const fund = funds.find((item) => String(item.id) === selected);
                  return (
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#293139',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {fund?.schemeName || 'Scheme'}
                    </Typography>
                  );
                }}
                sx={{
                  height: '36px',
                  borderRadius: '74px',
                  backgroundColor: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D9DEE5',
                    borderRadius: '74px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#B8C0CB',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2057A6',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused, &:focus-within, &:focus-visible': {
                    outline: '3px solid #2057A6 !important',
                    outlineOffset: '2px !important',
                    borderRadius: '74px !important',
                  },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    paddingRight: '32px !important',
                  },
                  '& .MuiSelect-select:focus': {
                    outline: 'none',
                    backgroundColor: 'transparent',
                  },
                }}>
                <MenuItem value="">
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: '#293139',
                    }}>
                    All Schemes
                  </Typography>
                </MenuItem>
                {funds.map((fund) => (
                  <MenuItem key={fund.id} value={String(fund.id)}>
                    {fund.schemeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                flex: '1 1 220px',
                minWidth: 0,
                maxWidth: { xs: '100%', sm: 280 },
                position: 'relative',
                overflow: 'visible',
              }}>
              <Box
                component="button"
                type="button"
                aria-label="Select date range filter"
                onClick={(e: MouseEvent<HTMLButtonElement>) => setDateAnchorEl(e.currentTarget)}
                sx={{
                  width: '100%',
                  minWidth: 0,
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  border: '1px solid #D9DEE5',
                  borderRadius: '74px',
                  backgroundColor: '#FFFFFF',
                  cursor: 'pointer',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: 14,
                  fontWeight: 500,
                  color: fromDate || toDate ? '#293139' : '#5F6B7A',
                  textAlign: 'left',
                  transition: 'border-color 0.2s ease',
                  '&:hover': {
                    borderColor: '#B8C0CB',
                  },
                  '&:focus': {
                    outline: '3px solid #2057A6',
                    outlineOffset: '2px',
                    borderColor: '#2057A6',
                    borderRadius: '74px',
                  },
                }}>
                <CardMedia
                  component="img"
                  src="/images/calender.svg"
                  alt=""
                  sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: 12, sm: 14 },
                    fontWeight: 500,
                    color: fromDate || toDate ? '#293139' : '#5F6B7A',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {dateLabel}
                </Typography>
              </Box>
              <Popover
                open={Boolean(dateAnchorEl)}
                anchorEl={dateAnchorEl}
                onClose={() => setDateAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    px: 2,
                    pt: 1.5,
                    pb: 1.5,
                    width: {
                      xs: 300,
                      sm: 320,
                    },
                    borderRadius: '10px',
                    boxShadow: '0px 8px 24px rgba(26, 43, 60, 0.12)',
                    border: '1px solid #E8ECF0',
                    overflow: 'hidden',
                  },
                }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box
                    sx={{
                      '& .MuiPickerStaticWrapper-root': {
                        minWidth: 'unset',
                      },
                      '& .MuiCalendarPicker-root': {
                        width: '100%',
                        maxHeight: 'unset',
                      },
                      '& .MuiPickersCalendarHeader-root': {
                        paddingLeft: 0,
                        paddingRight: 0,
                      },
                      '& .MuiPickersCalendarHeader-label': {
                        fontSize: 15,
                        fontWeight: 700,
                        color: '#293139',
                      },
                      '& .MuiDayPicker-header': {
                        justifyContent: 'space-between',
                      },
                      '& .MuiDayPicker-weekDayLabel': {
                        fontSize: 12,
                        color: '#9AA3AF',
                        fontWeight: 500,
                        width: 36,
                        height: 32,
                      },
                      '& .MuiDayPicker-weekContainer': {
                        justifyContent: 'space-between',
                        margin: 0,
                      },
                      '& .MuiDayPicker-slideTransition, & .MuiPickersSlideTransition-root, & .PrivatePickersSlideTransition-root':
                        {
                          minHeight: '240px !important',
                        },
                      '& .MuiDateRangePickerDay-root': {
                        width: 36,
                        height: 36,
                        margin: 0,
                      },
                    }}>
                    <StaticDateRangePicker
                      displayStaticWrapperAs="desktop"
                      calendars={1}
                      allowSameDateSelection
                      value={[fromDate, toDate]}
                      maxDate={new Date()}
                      onChange={(newValue) => {
                        const [start, end] = newValue;
                        setFromDate(start);
                        setToDate(end);
                        setCurrentPage(1);
                      }}
                      renderDay={(_day, dateRangePickerDayProps) => (
                        <RangeDay {...dateRangePickerDayProps} />
                      )}
                      renderInput={(startProps, endProps) => (
                        <>
                          <TextField {...startProps} sx={{ display: 'none' }} />
                          <TextField {...endProps} sx={{ display: 'none' }} />
                        </>
                      )}
                    />
                  </Box>
                </LocalizationProvider>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 0.5,
                  }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => {
                      setFromDate(null);
                      setToDate(null);
                      setCurrentPage(1);
                      setDateAnchorEl(null);
                    }}
                    sx={{
                      border: 'none',
                      background: 'transparent',
                      color: '#2057A6',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: 'Roboto, sans-serif',
                      p: 0,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}>
                    Clear dates
                  </Box>
                </Box>
              </Popover>
            </Box>

            {isCorporateTab && (
              <FormControl
                size="small"
                sx={{
                  flex: '1 1 160px',
                  minWidth: 0,
                  maxWidth: { xs: '100%', sm: 220 },
                }}>
                <Select
                  displayEmpty
                  value={companyId}
                  onChange={(event: SelectChangeEvent) => {
                    const selectedCompanyId = event.target.value;

                    setCompanyId(selectedCompanyId);
                    setCurrentPage(1);
                  }}
                  IconComponent={KeyboardArrowDownIcon}
                  inputProps={{ 'aria-label': 'Filter by company' }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}>
                          <CardMedia
                            component="img"
                            src="/images/company.svg"
                            alt="Company"
                            sx={{
                              width: 18,
                              height: 18,
                              objectFit: 'contain',
                            }}
                          />

                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: '#293139',
                            }}>
                            Company
                          </Typography>
                        </Box>
                      );
                    }

                    const selectedCompany = companies.find(
                      (company) => String(company.id) === String(selected)
                    );

                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}>
                        <CardMedia
                          component="img"
                          src="/images/company.svg"
                          alt="Company"
                          sx={{
                            width: 18,
                            height: 18,
                            objectFit: 'contain',
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: '#293139',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                          {selectedCompany?.name || 'Company'}
                        </Typography>
                      </Box>
                    );
                  }}
                  sx={{
                    height: '36px',
                    borderRadius: '74px',
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D9DEE5',
                      borderRadius: '74px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#B8C0CB',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2057A6',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused, &:focus-within, &:focus-visible': {
                      outline: '3px solid #2057A6 !important',
                      outlineOffset: '2px !important',
                      borderRadius: '74px !important',
                    },
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      paddingRight: '32px !important',
                    },
                    '& .MuiSelect-select:focus': {
                      outline: 'none',
                      backgroundColor: 'transparent',
                    },
                  }}>
                  <MenuItem value="">
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: '#293139',
                      }}>
                      All Companies
                    </Typography>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={String(company.id)}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl
              sx={{
                flex: '2 1 220px',
                minWidth: 0,
                maxWidth: '100%',
              }}>
              <Input
                fullWidth
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder={searchPlaceholder}
                inputProps={{ 'aria-label': searchPlaceholder }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchSharpIcon
                      sx={{
                        color: '#7B8794',
                        fontSize: 21,
                      }}
                    />
                  </InputAdornment>
                }
                disableUnderline
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  height: '36px',
                  bgcolor: '#FFFFFF',
                  border: '1px solid #D9DEE5',
                  borderRadius: '74px',
                  px: 1.5,
                  fontSize: 14,
                  fontFamily: 'Roboto, sans-serif',
                  '& input': {
                    py: 1.25,
                    minWidth: 0,
                  },
                  '& input::placeholder': {
                    color: '#8A94A6',
                    opacity: 1,
                  },
                  '&:focus-within': {
                    borderColor: '#2057A6',
                    outline: '3px solid #2057A6 !important',
                    outlineOffset: '2px !important',
                    borderRadius: '74px !important',
                  },
                  '&.Mui-focused': {
                    outline: '3px solid #2057A6 !important',
                    outlineOffset: '2px !important',
                    borderRadius: '74px !important',
                  },
                }}
              />
            </FormControl>
            {(schemeId || companyId || fromDate || toDate || search) && (
              <Box
                component="button"
                type="button"
                aria-label="Clear all filters"
                onClick={() => {
                  setSchemeId('');
                  setCompanyId('');
                  setFromDate(null);
                  setToDate(null);
                  setSearch('');
                  setCurrentPage(1);
                }}
                sx={{
                  border: 'none',
                  background: 'transparent',
                  color: '#2057A6',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'Roboto, sans-serif',
                  p: 0,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}>
                Clear Filters
              </Box>
            )}
          </Box>

          <DataTable
            tableData={rows}
            tableHeader={tableHeader}
            isLoading={isLoading}
            currentPage={currentPage}
            noOfPages={noOfPages}
            paginationAction={handlePageChange}
            boxStyles={{
              width: '100%',
              minWidth: 0,
              maxHeight: 'none',
              minHeight: { xs: 'auto', md: '480px' },
              overflowX: 'auto',
              '& .MuiTableContainer-root': {
                width: '100%',
                overflowX: 'auto',
              },
              '& .MuiTable-root': {
                width: '100%',
                minWidth: 960,
                borderCollapse: 'collapse',
              },
            }}
            tableHeaderCustomStyles={{
              '.MuiTableCell-head': {
                height: '48px',
                padding: '16px',
                fontFamily: 'Roboto, sans-serif',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '16px',
                letterSpacing: '0.02em',
                color: '#6C737F',
                backgroundColor: '#FFFFFF',
                borderBottom: '0.5px solid #CDCDCD',
                whiteSpace: 'nowrap',
                verticalAlign: 'middle',
              },
            }}
            tableBodyCustomStyles={{
              '.MuiTableRow-root': {
                height: '64px',
                backgroundColor: '#FFFFFF',
              },
              '.MuiTableCell-root': {
                height: '64px',
                padding: '16px',
                fontFamily: 'Roboto, sans-serif',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '16px',
                color: '#293139',
                borderBottom: '0.5px solid #CDCDCD',
                whiteSpace: 'nowrap',
                verticalAlign: 'middle',
              },
              '.MuiTableRow-root:hover': {
                backgroundColor: '#FFFFFF',
              },
            }}
          />
        </Box>
      </Box>
    </CommonLayout>
  );
}
